import {useEffect, useRef, useState} from 'react';
import {DatePicker, Form, Select, Spin, Steps, Switch} from 'antd';
import {useQuery} from '@apollo/client';
import {toast} from 'react-toastify';
import {Button} from '@dabafinance/react-components';
import {useNavigate} from 'react-router-dom';
import SubmitButton from 'components/Startup/components/SubmitButton';
import backIcon from 'images/back.png';
import {fieldRules, getUserTimezone} from 'utils/helper';
import {supportedLanguages} from 'utils/constants';
import {announcementType} from 'utils/mock';
import {GET_ALL_REPORT_ID} from 'graphql/queries/report';
import {
  GET_MINIMAL_CAMPAIGNS,
  GET_ALL_USERS_BY_NAME,
} from 'graphql/queries/campaign';
import {GET_FULL_SCREEN_ANNOUNCEMENT} from 'graphql/queries/announcement';
import {StepTwoAnnouncement} from '../popupAnnouncement/stepTwoAnnouncement';
import {useFullScreenPopup} from './useFullScreenPopup';
import {StepFullScreenContent} from './StepFullScreenContent';
import {StepFullScreenReview} from './StepFullScreenReview';
import {SubscriptionRenewalSection} from './SubscriptionRenewalSection';
import {mapFullScreenToForm} from './fullScreenHelpers';
import {
  AnnouncementEntitySelector,
  isSingleEntityType,
} from '../components/AnnouncementEntitySelector';
import styles from './fullScreenPopup.module.scss';

const PURPOSE_OPTIONS = [
  {value: 'GENERAL', label: 'General'},
  {value: 'DABA_PRO', label: 'Daba Pro'},
  {value: 'DABA_ACADEMY', label: 'Daba Academy'},
  {value: 'SIMULATOR', label: 'Simulator'},
];

// Purposes that unlock the subscription renewal (plan + catalog tabs + discount).
const RENEWAL_PURPOSES = ['DABA_PRO', 'DABA_ACADEMY'];

const STEPS = [
  {title: 'Settings'},
  {title: 'Target Audience'},
  {title: 'Content'},
  {title: 'Review'},
];

export const FullScreenPopupAnnouncement = ({editId}) => {
  const navigate = useNavigate();
  const popup = useFullScreenPopup();
  const {form} = popup;
  const [step, setStep] = useState(0);
  const isEdit = !!editId;

  const {Option} = Select;
  const type = Form.useWatch('type', form);
  // See PopupAnnouncement — one enum value, three instrument families.
  const opportunityKind = Form.useWatch('opportunityKind', form);
  const setOpportunityKind = value =>
    form.setFieldsValue({opportunityKind: value});
  const purpose = Form.useWatch('purpose', form);
  const showRenewal = RENEWAL_PURPOSES.includes(purpose);

  const {data: getAllReports, loading: loadingReports} = useQuery(
    GET_ALL_REPORT_ID,
    {
      errorPolicy: 'all',
      onError: error => toast.error(error.message),
    },
  );

  const {data: getAllCampaigns, loading: loadingCampaigns} = useQuery(
    GET_MINIMAL_CAMPAIGNS,
    {
      errorPolicy: 'all',
      onError: error => toast.error(error.message),
    },
  );

  // Edit mode: fetch the announcement and hydrate the form once.
  const {data: editData, loading: loadingEdit} = useQuery(
    GET_FULL_SCREEN_ANNOUNCEMENT,
    {
      skip: !editId,
      variables: {announcementId: editId},
      fetchPolicy: 'cache-and-network',
      errorPolicy: 'all',
      onError: error => toast.error(error.message),
    },
  );
  const announcement = editData?.getAnnouncement?.announcement;
  const editUserIds = announcement?.targetAudience?.userIds;

  const hydratedRef = useRef(false);
  useEffect(() => {
    if (!hydratedRef.current && announcement) {
      form.setFieldsValue(mapFullScreenToForm(announcement));
      hydratedRef.current = true;
    }
  }, [announcement, form]);

  // Resolve targeted user ids to the picker's "name;...;id;email" display format.
  const {data: targetedUsersData} = useQuery(GET_ALL_USERS_BY_NAME, {
    skip: !editUserIds?.length,
    variables: {filters: {user: {userIds: editUserIds}}},
    errorPolicy: 'all',
  });
  useEffect(() => {
    const users = targetedUsersData?.getAllUsers?.users;
    if (users?.length) {
      form.setFieldValue(
        'userIds',
        users.map(
          user => `${user.firstName};${user.lastName};${user.id};${user.email}`,
        ),
      );
    }
  }, [targetedUsersData, form]);

  // Each step's "Proceed" submit validates the mounted step; the final review
  // step has its own create button, so just guard against advancing past it.
  const handleStepSubmit = () => {
    if (step === 3) return;
    setStep(step + 1);
  };

  if (isEdit && loadingEdit && !announcement) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>
          <Spin size="large" />
          <h3>Loading announcement...</h3>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.navigation}>
        <img src={backIcon} alt="Go back" onClick={() => navigate(-1)} />
        <span className={styles.navTitle}>
          {isEdit ? 'Edit' : 'Create'} Full Screen Popup
        </span>
      </div>

      <div className={styles.header}>
        <Steps current={step} items={STEPS} className={styles.steps} />
      </div>

      <Form onFinish={handleStepSubmit} form={form} layout="vertical">
        {step === 0 && (
          <div className={styles.stepContainer}>
            <h2>Settings</h2>
            <p>Set the language, type, purpose and schedule for the popup.</p>
            <Form.Item
              rules={[fieldRules('Language')]}
              label="Language"
              className={styles.fieldWidth}
              name="language">
              <Select placeholder="Select language">
                {supportedLanguages.map(language => (
                  <Option key={language.value} value={language.value}>
                    {language.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              label="Announcement Type"
              className={styles.fieldWidth}
              name="type"
              rules={[fieldRules('Announcement Type')]}>
              <Select showSearch placeholder="Select type">
                {announcementType.map(({key, name}) => (
                  <Option key={key} value={key}>
                    {name}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              label="Purpose"
              className={styles.fieldWidth}
              name="purpose"
              tooltip="Daba Pro / Daba Academy unlock the subscription renewal offer."
              rules={[fieldRules('Purpose')]}>
              <Select placeholder="Select purpose" options={PURPOSE_OPTIONS} />
            </Form.Item>

            {showRenewal && <SubscriptionRenewalSection form={form} />}

            {type === 'SINGLE_REPORT' && (
              <Form.Item
                rules={[fieldRules('Report')]}
                name="report"
                className={styles.fieldWidth}
                label="Report">
                <Select
                  loading={loadingReports}
                  allowClear
                  placeholder="Select a report">
                  {getAllReports?.getAllReports?.map(({id, title}) => (
                    <Option key={id} value={id}>
                      {title}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            )}

            {type === 'SINGLE_OPPORTUNITY' && (
              <Form.Item
                rules={[fieldRules('Opportunity')]}
                name="opportunity"
                className={styles.fieldWidth}
                label="Opportunity">
                <Select
                  loading={loadingCampaigns}
                  allowClear
                  placeholder="Select an opportunity">
                  {getAllCampaigns?.getAllCampaigns?.campaigns?.map(
                    campaign => (
                      <Option key={campaign.id} value={campaign.id}>
                        {campaign.fundraisingCompany?.companyName}
                      </Option>
                    ),
                  )}
                </Select>
              </Form.Item>
            )}

            {isSingleEntityType(type) && (
              <AnnouncementEntitySelector
                type={type}
                opportunityKind={opportunityKind}
                onOpportunityKindChange={setOpportunityKind}
              />
            )}

            <Form.Item
              label="Open URL on click?"
              className={styles.fieldWidth}
              name="openUrlOnClick"
              valuePropName="checked">
              <Switch />
            </Form.Item>

            <div className={styles.dateFields}>
              <Form.Item
                label="Published Date (Time is saved in UTC/GMT)"
                name="publishedDate"
                className={styles.fieldWidth}
                rules={[fieldRules('Published Date')]}>
                <DatePicker
                  renderExtraFooter={() => (
                    <div style={{textAlign: 'center'}}>
                      <p>Your timezone is: {getUserTimezone()}</p>
                    </div>
                  )}
                  format={'YYYY-MM-DD HH:mm:ss'}
                  showTime
                />
              </Form.Item>
              <Form.Item
                rules={[fieldRules('Expiry Date')]}
                name="expiredDate"
                className={styles.fieldWidth}
                label="Expiration Date (Time is saved in UTC/GMT)">
                <DatePicker
                  format={'YYYY-MM-DD HH:mm:ss'}
                  renderExtraFooter={() => (
                    <div style={{textAlign: 'center'}}>
                      <p>Your timezone is: {getUserTimezone()}</p>
                    </div>
                  )}
                  showTime
                />
              </Form.Item>
            </div>
          </div>
        )}

        {step === 1 && (
          <div className={styles.stepContainer}>
            <h2>Target Audience</h2>
            <p>
              Target specific users, or use the filters below. This applies to
              all announcement types.
            </p>
            <StepTwoAnnouncement
              form={form}
              isEdit
              defaultUserIds={editUserIds}
            />
          </div>
        )}

        {step === 2 && <StepFullScreenContent form={form} popup={popup} />}

        {step === 3 && (
          <StepFullScreenReview
            form={form}
            onBack={() => setStep(2)}
            editId={editId}
          />
        )}

        {step < 3 && (
          <div className={styles.stepActions}>
            {step > 0 && (
              <Button label="Back" onClick={() => setStep(step - 1)} />
            )}
            <SubmitButton label="Proceed to Next Step" smallWidth />
          </div>
        )}
      </Form>
    </div>
  );
};
